import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { fetchBlogPost } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useSEO, articleJsonLd, breadcrumbJsonLd, faqPageJsonLd, siteUrl } from "@/lib/seo";
import CTASection from "@/components/CTASection";

// Markdown -> premium dark-theme components. Internal links stay client-side via <Link>.
const mdComponents = {
  h2: ({ node, ...props }) => (
    <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mt-12 mb-4" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="text-xl sm:text-2xl font-semibold text-white tracking-tight mt-8 mb-3" {...props} />
  ),
  p: ({ node, ...props }) => (
    <p className="text-white/70 text-[17px] leading-[1.85] mb-5" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="space-y-2.5 text-white/70 text-[17px] leading-[1.7] mb-6 pl-5 list-disc marker:text-[#00D4FF]" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="space-y-2.5 text-white/70 text-[17px] leading-[1.7] mb-6 pl-5 list-decimal marker:text-[#00D4FF]" {...props} />
  ),
  li: ({ node, ...props }) => <li className="pl-1" {...props} />,
  strong: ({ node, ...props }) => <strong className="text-white font-semibold" {...props} />,
  em: ({ node, ...props }) => <em className="text-white/85" {...props} />,
  a: ({ node, href, children, ...props }) => {
    const isInternal = href && href.startsWith("/");
    if (isInternal) {
      return (
        <Link to={href} className="text-[#00D4FF] hover:text-white underline underline-offset-4 decoration-[#00D4FF]/40 hover:decoration-white transition-colors">
          {children}
        </Link>
      );
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#00D4FF] hover:text-white underline underline-offset-4" {...props}>
        {children}
      </a>
    );
  },
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-2 border-[#00D4FF]/60 pl-5 py-1 my-6 text-white/75 italic" {...props} />
  ),
  code: ({ node, inline, ...props }) => (
    inline ? (
      <code className="px-1.5 py-0.5 rounded bg-white/10 text-[#00D4FF] text-[0.9em] mono" {...props} />
    ) : (
      <code className="block p-4 rounded-lg bg-[#050509] border border-white/10 text-white/80 text-sm overflow-x-auto mono" {...props} />
    )
  ),
};

const BlogPost = () => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  // 'loading' | 'success' | 'notfound' | 'error'
  // 'notfound' = API returned real 404 for this slug
  // 'error'    = transient network / server / timeout failure (do NOT redirect)
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    setPost(null);
    setStatus("loading");

    const maxAttempts = 3;
    let attempt = 0;

    const load = async () => {
      while (attempt < maxAttempts && !cancelled) {
        attempt += 1;
        try {
          const data = await fetchBlogPost(slug);
          if (cancelled) return;
          setPost(data);
          setStatus("success");
          return;
        } catch (err) {
          if (cancelled) return;
          const httpStatus = err?.response?.status;
          // Only a *real* 404 from the API means "slug does not exist".
          // Any other failure (network error, 5xx, timeout, CORS) is transient.
          if (httpStatus === 404) {
            setStatus("notfound");
            return;
          }
          if (attempt >= maxAttempts) {
            setStatus("error");
            return;
          }
          // Exponential backoff: 500ms, 1500ms
          await new Promise((r) => setTimeout(r, 500 * Math.pow(3, attempt - 1)));
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    import("@/lib/api").then((m) => m.fetchBlog().then(setAllPosts).catch(() => {}));
  }, []);

  // Canonical & title moeten ALTIJD direct gezet worden o.b.v. de URL slug —
  // niet pas na fetchBlogPost(). Anders erft de eerste render de homepage-canonical
  // uit /public/index.html, waardoor Googlebot "Pagina met omleiding" rapporteert
  // (soft redirect via canonical mismatch).
  // Sommige artikelen hebben een afwijkende canonical-URL (bijv. /kennisbank/…).
  // Tot het post-object geladen is gebruiken we de huidige route als canonical,
  // daarna schakelen we over naar het expliciete `url_path` van de post als die bestaat.
  const fallbackPath = window.location.pathname.replace(/\/$/, "") || `/blog/${slug}`;
  const canonicalPath = post?.url_path || fallbackPath;
  const canonical = siteUrl(canonicalPath);
  const articleJsonLdData = post ? articleJsonLd(post, lang) : null;
  if (articleJsonLdData) {
    articleJsonLdData.mainEntityOfPage = {
      "@type": "WebPage",
      "@id": canonical,
    };
  }
  useSEO({
    title: post ? `${post[`title_${lang}`]} | Wasz.nl` : `Kennisbank | Wasz.nl`,
    description: post
      ? post[`excerpt_${lang}`]
      : lang === "nl"
      ? "Kennisbank van Wasz.nl — praktische inzichten over mobiele truckwash, HACCP en wagenparkbeheer."
      : "Wasz.nl knowledge base — practical insights on mobile truckwash, HACCP and fleet management.",
    canonical,
    image: post ? post.image : siteUrl("/og-image-ai.jpg?v=4"),
    lang,
    jsonLd: post
      ? [
          { id: "article-jsonld", data: articleJsonLdData },
          {
            id: "article-breadcrumb",
            data: breadcrumbJsonLd([
              { name: "Home", url: siteUrl("/") },
              { name: lang === "nl" ? "Kennisbank" : "Knowledge Base", url: siteUrl("/blog") },
              { name: post[`title_${lang}`], url: canonical },
            ]),
          },
          ...(Array.isArray(post.faqs) && post.faqs.length > 0
            ? [{ id: "article-faq", data: faqPageJsonLd(post.faqs, lang) }]
            : []),
        ]
      : [],
  });

  // Real 404 — the API confirmed this slug does not exist. Render an inline
  // "article not found" state on the same URL. No client-side route change,
  // no canonical mutation to /blog (which previously caused GSC "Page with redirect").
  if (status === "notfound") {
    return (
      <main
        data-testid="blog-post-notfound"
        className="min-h-screen pt-32 pb-24"
      >
        <div className="mx-auto max-w-2xl px-6 lg:px-8 text-center">
          <p className="section-overline mb-3">404</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
            {lang === "nl" ? "Artikel niet gevonden" : "Article not found"}
          </h1>
          <p className="text-white/60 leading-relaxed mb-8">
            {lang === "nl"
              ? "Dit artikel bestaat niet (meer). Bekijk onze kennisbank voor alle beschikbare artikelen."
              : "This article does not (or no longer) exist. Browse our knowledge base for all available articles."}
          </p>
          <Link
            to="/blog"
            data-testid="blog-post-notfound-link"
            className="inline-flex items-center gap-1.5 text-[#00D4FF] hover:text-white text-sm font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />{" "}
            {lang === "nl" ? "Naar de kennisbank" : "Back to knowledge base"}
          </Link>
        </div>
      </main>
    );
  }

  // Transient error — network / timeout / 5xx. Stay on the same URL, show a
  // discreet retry message. Crucially: do NOT redirect to /blog, otherwise the
  // canonical on this URL becomes /blog and Google reports "Page with redirect".
  if (status === "error") {
    return (
      <main
        data-testid="blog-post-error"
        className="min-h-screen pt-32 pb-24"
      >
        <div className="mx-auto max-w-2xl px-6 lg:px-8 text-center">
          <p className="text-white/60 leading-relaxed mb-6">
            {lang === "nl"
              ? "Het laden van dit artikel duurde langer dan verwacht. Ververs de pagina om het opnieuw te proberen."
              : "Loading this article took longer than expected. Refresh the page to try again."}
          </p>
          <button
            type="button"
            data-testid="blog-post-error-retry"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 text-[#00D4FF] hover:text-white text-sm font-semibold"
          >
            {lang === "nl" ? "Opnieuw proberen" : "Try again"}
          </button>
        </div>
      </main>
    );
  }

  // Still loading (or retrying transient error) — stay on the URL, show spinner.
  if (!post) {
    return (
      <main
        data-testid="blog-post-loading"
        className="min-h-screen pt-32 text-white/60 text-center"
      >
        Loading…
      </main>
    );
  }

  const content = post[`content_${lang}`] || post.content_nl || "";
  const related = (allPosts || [])
    .filter((p) => p.slug !== post.slug)
    .slice(0, 3);

  return (
    <main data-testid={`blog-post-${post.slug}`} className="min-h-screen">
      <article className="pt-20 pb-20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          {/* Visible breadcrumb: Home > Kennisbank > Artikel */}
          <nav
            aria-label={lang === "nl" ? "Kruimelpad" : "Breadcrumb"}
            data-testid={`blog-breadcrumb-${post.slug}`}
            className="mb-8 text-xs text-white/45"
          >
            <ol className="flex flex-wrap items-center gap-x-1.5">
              <li>
                <Link to="/" className="hover:text-[#00D4FF]">
                  Home
                </Link>
              </li>
              <li className="text-white/30">/</li>
              <li>
                <Link to="/blog" className="hover:text-[#00D4FF]">
                  {lang === "nl" ? "Kennisbank" : "Knowledge Base"}
                </Link>
              </li>
              <li className="text-white/30">/</li>
              <li className="text-white/70 line-clamp-1">
                {post[`title_${lang}`]}
              </li>
            </ol>
          </nav>

          <p className="section-overline mb-3">{post[`category_${lang}`]}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05]">
            {post[`title_${lang}`]}
          </h1>
          <div className="mt-6 flex items-center gap-5 text-sm text-white/40">
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {post.date}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {post.read_time} min</span>
          </div>
        </div>

        <div className="mx-auto max-w-5xl px-6 lg:px-8 mt-12">
          <div className="aspect-[16/9] rounded-2xl overflow-hidden border border-white/10">
            <img src={post.image} alt={post[`title_${lang}`]} className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-6 lg:px-8 mt-12">
          <p className="text-xl text-white/85 leading-relaxed font-light border-l-2 border-[#00D4FF]/60 pl-5">
            {post[`excerpt_${lang}`]}
          </p>
          <div className="mt-10" data-testid={`blog-post-content-${post.slug}`}>
            <ReactMarkdown components={mdComponents}>{content}</ReactMarkdown>
          </div>

          {/* Back to blog link below content */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <Link
              to="/blog"
              className="text-xs text-white/50 hover:text-[#00D4FF] inline-flex items-center gap-1.5"
            >
              <ArrowLeft className="w-3 h-3" /> {t("common.back")}
            </Link>
          </div>
        </div>
      </article>

      {/* GERELATEERDE ARTIKELEN — internal linking between blog posts */}
      {related.length > 0 && (
        <section
          data-testid={`blog-related-${post.slug}`}
          className="py-20 sm:py-24 bg-[#0a0a18] border-t border-white/5"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mb-10">
              <p className="section-overline mb-3">{lang === "nl" ? "Lees verder" : "Read more"}</p>
              <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-white leading-tight">
                {lang === "nl" ? "Gerelateerde artikelen." : "Related articles."}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={p.url_path || `/blog/${p.slug}`}
                  data-testid={`blog-related-card-${p.slug}`}
                  className="group relative rounded-2xl border border-white/10 bg-[#0F0F23] overflow-hidden card-hover flex flex-col"
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={p.image}
                      alt={p[`title_${lang}`]}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded bg-[#050509]/70 backdrop-blur text-[10px] uppercase tracking-wider font-semibold text-[#00D4FF] border border-[#00D4FF]/30">
                      {p[`category_${lang}`]}
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="text-base font-bold text-white tracking-tight mb-2 leading-snug line-clamp-2">{p[`title_${lang}`]}</h3>
                    <p className="text-white/55 text-sm leading-relaxed line-clamp-3 flex-1">{p[`excerpt_${lang}`]}</p>
                    <div className="mt-4 flex items-center justify-between text-xs text-white/40">
                      <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {p.date}</span>
                      <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {p.read_time} min</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </main>
  );
};

export default BlogPost;
